/**
 * 未管理自動登録で補完対象となるプレースホルダーフィールド
 */
export const unmanagedBusinessPlaceholderFieldValues = [
  'supplierName',
  'supplierNumber',
  'businessName',
  'businessNumber',
  'warehouse',
  'warehouseNumber',
  'package',
  'customerName',
  'customerNumber',
  'salesDestinationName',
  'salesDestinationNumber',
  'manufacturer',
  'endUser',
] as const;

/**
 * プレースホルダーフィールドの型
 */
export type UnmanagedBusinessPlaceholderField = (typeof unmanagedBusinessPlaceholderFieldValues)[number];

/**
 * 自動登録条件で選択できる対象項目
 */
export const unmanagedBusinessAutoRegisterFieldValues = [
  'supplierNumber',
  'supplierName',
  'businessNumber',
  'businessName',
  'warehouseNumber',
  'warehouse',
  'package',
  'customerNumber',
  'customerName',
  'salesDestinationNumber',
  'salesDestinationName',
  'unit',
  'manufacturer',
  'endUser',
  'orderPackage',
  'moq',
  'forecastForReceiptMemo',
  'purchaseUnitPrice',
  'salesUnitPrice',
] as const;

/**
 * 条件画面で選択する条件項目
 */
export const unmanagedBusinessAutoRegisterConditionFieldTypeValues = [
  'supplier',
  'business',
  'warehouse',
  'package',
  'customer',
  'salesDestination',
] as const;

/**
 * 条件画面で選択する検索方法
 */
export const unmanagedBusinessAutoRegisterConditionSearchTypeValues = ['code', 'name', 'value'] as const;

/**
 * 登録項目で使用する単位
 */
export const unmanagedBusinessAutoRegisterRegisterUnitValues = ['day', 'week', 'month'] as const;

/**
 * 自動登録条件で指定する項目の型
 */
export type UnmanagedBusinessAutoRegisterField = (typeof unmanagedBusinessAutoRegisterFieldValues)[number];

/**
 * 条件項目の型
 */
export type UnmanagedBusinessAutoRegisterConditionFieldType = (typeof unmanagedBusinessAutoRegisterConditionFieldTypeValues)[number];

/**
 * 検索方法の型
 */
export type UnmanagedBusinessAutoRegisterConditionSearchType = (typeof unmanagedBusinessAutoRegisterConditionSearchTypeValues)[number];

/**
 * 登録項目単位の型
 */
export type UnmanagedBusinessAutoRegisterRegisterUnit = (typeof unmanagedBusinessAutoRegisterRegisterUnitValues)[number];

/**
 * 新仕様の条件1件分
 */
export interface UnmanagedBusinessAutoRegisterSettingConditionItem {
  targetFieldType: UnmanagedBusinessAutoRegisterConditionFieldType;
  searchType: UnmanagedBusinessAutoRegisterConditionSearchType;
  targetValue: string;
  targetName?: string;
}

/**
 * 新仕様の任意登録項目
 */
export interface UnmanagedBusinessAutoRegisterOptionalRegisterItem {
  targetField: UnmanagedBusinessAutoRegisterField;
  targetValue: string;
}

/**
 * 条件一致時の登録項目
 */
export interface UnmanagedBusinessAutoRegisterRegisterContent {
  leadTime: number;
  leadTimeUnit: UnmanagedBusinessAutoRegisterRegisterUnit;
  stdInventoryIndicesMin: number;
  stdInventoryIndicesMax: number;
  stdInventoryIndicesUnit: UnmanagedBusinessAutoRegisterRegisterUnit;
  optionalItems: UnmanagedBusinessAutoRegisterOptionalRegisterItem[];
}

/**
 * 新仕様の設定1件分
 */
export interface UnmanagedBusinessAutoRegisterSettingItem {
  settingId: string;
  settingName: string;
  createdAt: number;
  conditions: UnmanagedBusinessAutoRegisterSettingConditionItem[];
  registerContent: UnmanagedBusinessAutoRegisterRegisterContent;
}

/**
 * 未管理取引の自動登録条件
 */
export interface UnmanagedBusinessAutoRegisterCondition {
  enabled: boolean;
  settings: UnmanagedBusinessAutoRegisterSettingItem[];
}

const defaultTargetField: UnmanagedBusinessAutoRegisterField = 'customerNumber';
const defaultConditionFieldType: UnmanagedBusinessAutoRegisterConditionFieldType = 'customer';
const defaultRegisterUnit: UnmanagedBusinessAutoRegisterRegisterUnit = 'day';
const unmanagedBusinessAutoRegisterFieldValueSet = new Set<UnmanagedBusinessAutoRegisterField>(unmanagedBusinessAutoRegisterFieldValues);
const unmanagedBusinessAutoRegisterConditionFieldTypeSet = new Set<UnmanagedBusinessAutoRegisterConditionFieldType>(
  unmanagedBusinessAutoRegisterConditionFieldTypeValues
);
const unmanagedBusinessAutoRegisterConditionSearchTypeSet = new Set<UnmanagedBusinessAutoRegisterConditionSearchType>(
  unmanagedBusinessAutoRegisterConditionSearchTypeValues
);
const unmanagedBusinessAutoRegisterRegisterUnitSet = new Set<UnmanagedBusinessAutoRegisterRegisterUnit>(
  unmanagedBusinessAutoRegisterRegisterUnitValues
);

const targetFieldByConditionKey: Record<
  UnmanagedBusinessAutoRegisterConditionFieldType,
  Partial<Record<UnmanagedBusinessAutoRegisterConditionSearchType, UnmanagedBusinessAutoRegisterField>>
> = {
  supplier: {
    code: 'supplierNumber',
    name: 'supplierName',
  },
  business: {
    code: 'businessNumber',
    name: 'businessName',
  },
  warehouse: {
    code: 'warehouseNumber',
    name: 'warehouse',
  },
  package: {
    value: 'package',
  },
  customer: {
    code: 'customerNumber',
    name: 'customerName',
  },
  salesDestination: {
    code: 'salesDestinationNumber',
    name: 'salesDestinationName',
  },
};

const conditionKeyByTargetField: Partial<
  Record<
    UnmanagedBusinessAutoRegisterField,
    {
      targetFieldType: UnmanagedBusinessAutoRegisterConditionFieldType;
      searchType: UnmanagedBusinessAutoRegisterConditionSearchType;
    }
  >
> = {
  supplierNumber: {
    targetFieldType: 'supplier',
    searchType: 'code',
  },
  supplierName: {
    targetFieldType: 'supplier',
    searchType: 'name',
  },
  businessNumber: {
    targetFieldType: 'business',
    searchType: 'code',
  },
  businessName: {
    targetFieldType: 'business',
    searchType: 'name',
  },
  warehouseNumber: {
    targetFieldType: 'warehouse',
    searchType: 'code',
  },
  warehouse: {
    targetFieldType: 'warehouse',
    searchType: 'name',
  },
  package: {
    targetFieldType: 'package',
    searchType: 'value',
  },
  customerNumber: {
    targetFieldType: 'customer',
    searchType: 'code',
  },
  customerName: {
    targetFieldType: 'customer',
    searchType: 'name',
  },
  salesDestinationNumber: {
    targetFieldType: 'salesDestination',
    searchType: 'code',
  },
  salesDestinationName: {
    targetFieldType: 'salesDestination',
    searchType: 'name',
  },
};

const defaultSearchTypeByFieldType: Record<
  UnmanagedBusinessAutoRegisterConditionFieldType,
  UnmanagedBusinessAutoRegisterConditionSearchType
> = {
  supplier: 'code',
  business: 'code',
  warehouse: 'code',
  package: 'value',
  customer: 'code',
  salesDestination: 'code',
};

/**
 * 新仕様の条件1件分の初期値を作成する
 */
export const createDefaultUnmanagedBusinessAutoRegisterSettingConditionItem = (): UnmanagedBusinessAutoRegisterSettingConditionItem => ({
  targetFieldType: defaultConditionFieldType,
  searchType: defaultSearchTypeByFieldType[defaultConditionFieldType],
  targetValue: '',
});

/**
 * 新仕様の任意登録項目の初期値を作成する
 */
export const createDefaultUnmanagedBusinessAutoRegisterOptionalRegisterItem = (): UnmanagedBusinessAutoRegisterOptionalRegisterItem => ({
  targetField: 'unit',
  targetValue: '',
});

/**
 * 新仕様の登録項目初期値を作成する
 */
export const createDefaultUnmanagedBusinessAutoRegisterRegisterContent = (): UnmanagedBusinessAutoRegisterRegisterContent => ({
  leadTime: 0,
  leadTimeUnit: defaultRegisterUnit,
  stdInventoryIndicesMin: 0,
  stdInventoryIndicesMax: 0,
  stdInventoryIndicesUnit: defaultRegisterUnit,
  optionalItems: [],
});

/**
 * 新仕様の設定IDを生成する
 */
export const createUnmanagedBusinessAutoRegisterSettingId = (): string => {
  return `ub-setting-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
};

/**
 * 新仕様の設定1件分の初期値を作成する
 */
export const createDefaultUnmanagedBusinessAutoRegisterSettingItem = (): UnmanagedBusinessAutoRegisterSettingItem => ({
  settingId: createUnmanagedBusinessAutoRegisterSettingId(),
  settingName: '',
  createdAt: Date.now(),
  conditions: [createDefaultUnmanagedBusinessAutoRegisterSettingConditionItem()],
  registerContent: createDefaultUnmanagedBusinessAutoRegisterRegisterContent(),
});

/**
 * 未管理取引の自動登録条件の初期値を作成する
 */
export const createDefaultUnmanagedBusinessAutoRegisterCondition = (): UnmanagedBusinessAutoRegisterCondition => ({
  enabled: false,
  settings: [],
});

/**
 * 未管理取引の自動登録条件の初期値
 */
export const defaultUnmanagedBusinessAutoRegisterCondition = createDefaultUnmanagedBusinessAutoRegisterCondition();

/**
 * 自動登録条件の項目表示名
 */
export const unmanagedBusinessAutoRegisterFieldLabels: Record<UnmanagedBusinessAutoRegisterField, string> = {
  supplierNumber: '仕入先CD',
  supplierName: '仕入先名',
  businessNumber: '商品CD',
  businessName: '商品名',
  warehouseNumber: '置場CD',
  warehouse: '置場名',
  package: '容量',
  customerNumber: '売上先CD',
  customerName: '売上先名',
  salesDestinationNumber: '送り先CD',
  salesDestinationName: '送り先名',
  unit: '単位',
  endUser: 'エンドユーザー',
  manufacturer: '製造社',
  orderPackage: '発注単位',
  moq: 'MOQ',
  forecastForReceiptMemo: '受領用内示名',
  purchaseUnitPrice: '仕入単価',
  salesUnitPrice: '販売単価',
};

/**
 * 条件項目の表示名
 */
export const unmanagedBusinessAutoRegisterConditionFieldTypeLabels: Record<UnmanagedBusinessAutoRegisterConditionFieldType, string> = {
  supplier: '仕入先',
  business: '商品',
  warehouse: '置場',
  package: '容量',
  customer: '売上先',
  salesDestination: '送り先',
};

/**
 * 検索方法の表示名
 */
export const unmanagedBusinessAutoRegisterConditionSearchTypeLabels: Record<UnmanagedBusinessAutoRegisterConditionSearchType, string> = {
  code: 'コード',
  name: '名称',
  value: '値',
};

/**
 * 登録項目単位の表示名
 */
export const unmanagedBusinessAutoRegisterRegisterUnitLabels: Record<UnmanagedBusinessAutoRegisterRegisterUnit, string> = {
  day: '日',
  week: '週',
  month: '月',
};

/**
 * 条件項目と検索方法から比較対象フィールドへ変換する
 * @param targetFieldType - 条件項目
 * @param searchType - 検索方法
 */
export const convertConditionKeyToTargetField = (
  targetFieldType: UnmanagedBusinessAutoRegisterConditionFieldType,
  searchType: UnmanagedBusinessAutoRegisterConditionSearchType
): UnmanagedBusinessAutoRegisterField => {
  const fieldBySearchType = targetFieldByConditionKey[targetFieldType];
  if (fieldBySearchType[searchType]) {
    return fieldBySearchType[searchType] as UnmanagedBusinessAutoRegisterField;
  }

  return fieldBySearchType[defaultSearchTypeByFieldType[targetFieldType]] ?? defaultTargetField;
};

/**
 * 比較対象フィールドを条件項目と検索方法へ変換する
 * @param targetField - 比較対象フィールド
 */
export const convertTargetFieldToConditionKey = (
  targetField: UnmanagedBusinessAutoRegisterField
): {
  targetFieldType: UnmanagedBusinessAutoRegisterConditionFieldType;
  searchType: UnmanagedBusinessAutoRegisterConditionSearchType;
} => {
  return (
    conditionKeyByTargetField[targetField] ?? {
      targetFieldType: defaultConditionFieldType,
      searchType: defaultSearchTypeByFieldType[defaultConditionFieldType],
    }
  );
};

/**
 * 条件項目で選択可能な検索方法を返す
 * @param targetFieldType - 条件項目
 */
export const getConditionSearchTypesByFieldType = (
  targetFieldType: UnmanagedBusinessAutoRegisterConditionFieldType
): UnmanagedBusinessAutoRegisterConditionSearchType[] => {
  const fieldBySearchType = targetFieldByConditionKey[targetFieldType];
  return unmanagedBusinessAutoRegisterConditionSearchTypeValues.filter((searchType) => fieldBySearchType[searchType] != null);
};

/**
 * 新仕様設定1件分をディープコピーする
 * @param setting - コピー対象
 */
export const cloneUnmanagedBusinessAutoRegisterSettingItem = (
  setting: UnmanagedBusinessAutoRegisterSettingItem
): UnmanagedBusinessAutoRegisterSettingItem => ({
  settingId: setting.settingId,
  settingName: setting.settingName,
  createdAt: setting.createdAt,
  conditions: setting.conditions.map((conditionItem) => ({
    targetFieldType: conditionItem.targetFieldType,
    searchType: conditionItem.searchType,
    targetValue: conditionItem.targetValue,
    targetName: conditionItem.targetName,
  })),
  registerContent: {
    leadTime: setting.registerContent.leadTime,
    leadTimeUnit: setting.registerContent.leadTimeUnit,
    stdInventoryIndicesMin: setting.registerContent.stdInventoryIndicesMin,
    stdInventoryIndicesMax: setting.registerContent.stdInventoryIndicesMax,
    stdInventoryIndicesUnit: setting.registerContent.stdInventoryIndicesUnit,
    optionalItems: setting.registerContent.optionalItems.map((optionalItem) => ({
      targetField: optionalItem.targetField,
      targetValue: optionalItem.targetValue,
    })),
  },
});

/**
 * 自動登録条件をディープコピーする
 * @param condition - コピー対象の自動登録条件
 */
export const cloneUnmanagedBusinessAutoRegisterCondition = (
  condition: UnmanagedBusinessAutoRegisterCondition
): UnmanagedBusinessAutoRegisterCondition => ({
  enabled: condition.enabled,
  settings: condition.settings.map((setting) => cloneUnmanagedBusinessAutoRegisterSettingItem(setting)),
});

/**
 * 値がRecord型かどうかを判定する
 * @param value - 判定対象の値
 */
const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

/**
 * 項目値を自動登録対象フィールドへ正規化する
 * @param value - 正規化対象の項目値
 */
const normalizeTargetField = (value: unknown): UnmanagedBusinessAutoRegisterField => {
  if (typeof value === 'string' && unmanagedBusinessAutoRegisterFieldValueSet.has(value as UnmanagedBusinessAutoRegisterField)) {
    return value as UnmanagedBusinessAutoRegisterField;
  }
  return defaultTargetField;
};

/**
 * 条件項目を正規化する
 * @param value - 正規化対象
 */
const normalizeConditionFieldType = (value: unknown): UnmanagedBusinessAutoRegisterConditionFieldType => {
  if (
    typeof value === 'string' &&
    unmanagedBusinessAutoRegisterConditionFieldTypeSet.has(value as UnmanagedBusinessAutoRegisterConditionFieldType)
  ) {
    return value as UnmanagedBusinessAutoRegisterConditionFieldType;
  }
  return defaultConditionFieldType;
};

/**
 * 検索方法を正規化する
 * @param value - 正規化対象
 */
const normalizeConditionSearchType = (value: unknown): UnmanagedBusinessAutoRegisterConditionSearchType => {
  if (
    typeof value === 'string' &&
    unmanagedBusinessAutoRegisterConditionSearchTypeSet.has(value as UnmanagedBusinessAutoRegisterConditionSearchType)
  ) {
    return value as UnmanagedBusinessAutoRegisterConditionSearchType;
  }
  return defaultSearchTypeByFieldType[defaultConditionFieldType];
};

/**
 * 単位を正規化する
 * @param value - 正規化対象
 */
const normalizeRegisterUnit = (value: unknown): UnmanagedBusinessAutoRegisterRegisterUnit => {
  if (typeof value === 'string' && unmanagedBusinessAutoRegisterRegisterUnitSet.has(value as UnmanagedBusinessAutoRegisterRegisterUnit)) {
    return value as UnmanagedBusinessAutoRegisterRegisterUnit;
  }

  return defaultRegisterUnit;
};

/**
 * 数値を正規化する
 * @param value - 正規化対象
 * @param fallback - 不正値時のデフォルト値
 */
const normalizeNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallback;
};

/**
 * 任意登録項目を正規化する
 * @param value - 正規化対象
 */
const normalizeOptionalRegisterItem = (value: unknown): UnmanagedBusinessAutoRegisterOptionalRegisterItem => {
  if (!isObjectRecord(value)) {
    return createDefaultUnmanagedBusinessAutoRegisterOptionalRegisterItem();
  }

  return {
    targetField: normalizeTargetField(value.targetField),
    targetValue: typeof value.targetValue === 'string' ? value.targetValue : '',
  };
};

/**
 * 条件一致時の登録項目を正規化する
 * @param value - 正規化対象
 */
const normalizeRegisterContent = (value: unknown): UnmanagedBusinessAutoRegisterRegisterContent => {
  const defaultRegisterContent = createDefaultUnmanagedBusinessAutoRegisterRegisterContent();
  if (!isObjectRecord(value)) {
    return defaultRegisterContent;
  }

  const optionalItems = Array.isArray(value.optionalItems)
    ? value.optionalItems.map(normalizeOptionalRegisterItem)
    : defaultRegisterContent.optionalItems;

  return {
    leadTime: normalizeNumber(value.leadTime, defaultRegisterContent.leadTime),
    leadTimeUnit: normalizeRegisterUnit(value.leadTimeUnit),
    stdInventoryIndicesMin: normalizeNumber(value.stdInventoryIndicesMin, defaultRegisterContent.stdInventoryIndicesMin),
    stdInventoryIndicesMax: normalizeNumber(value.stdInventoryIndicesMax, defaultRegisterContent.stdInventoryIndicesMax),
    stdInventoryIndicesUnit: normalizeRegisterUnit(value.stdInventoryIndicesUnit),
    optionalItems,
  };
};

/**
 * 新仕様の条件1件分を正規化する
 * @param value - 正規化対象
 */
const normalizeSettingConditionItem = (value: unknown): UnmanagedBusinessAutoRegisterSettingConditionItem => {
  const defaultConditionItem = createDefaultUnmanagedBusinessAutoRegisterSettingConditionItem();
  if (!isObjectRecord(value)) {
    return defaultConditionItem;
  }

  const targetFieldType = normalizeConditionFieldType(value.targetFieldType);
  const rawSearchType = normalizeConditionSearchType(value.searchType);
  const availableSearchTypes = getConditionSearchTypesByFieldType(targetFieldType);
  const searchType = availableSearchTypes.includes(rawSearchType) ? rawSearchType : availableSearchTypes[0];

  return {
    targetFieldType,
    searchType,
    targetValue: typeof value.targetValue === 'string' ? value.targetValue : '',
    targetName: typeof value.targetName === 'string' ? value.targetName : undefined,
  };
};

/**
 * 新仕様の設定1件分を正規化する
 * @param value - 正規化対象
 * @param index - 設定番号
 */
const normalizeSettingItem = (value: unknown, index: number): UnmanagedBusinessAutoRegisterSettingItem => {
  const defaultSetting = createDefaultUnmanagedBusinessAutoRegisterSettingItem();
  if (!isObjectRecord(value)) {
    return {
      ...defaultSetting,
      settingName: `条件設定${index + 1}`,
    };
  }

  const conditions = Array.isArray(value.conditions)
    ? value.conditions.map(normalizeSettingConditionItem)
    : [createDefaultUnmanagedBusinessAutoRegisterSettingConditionItem()];

  return {
    settingId: typeof value.settingId === 'string' && value.settingId.trim() !== '' ? value.settingId : defaultSetting.settingId,
    settingName: typeof value.settingName === 'string' ? value.settingName : `条件設定${index + 1}`,
    createdAt: normalizeNumber(value.createdAt, defaultSetting.createdAt),
    conditions: conditions.length > 0 ? conditions : [createDefaultUnmanagedBusinessAutoRegisterSettingConditionItem()],
    registerContent: normalizeRegisterContent(value.registerContent),
  };
};

/**
 * 任意データを自動登録条件に正規化する
 * @param value - 正規化対象のデータ
 */
export const normalizeUnmanagedBusinessAutoRegisterCondition = (value: unknown): UnmanagedBusinessAutoRegisterCondition => {
  const defaultCondition = createDefaultUnmanagedBusinessAutoRegisterCondition();

  if (!isObjectRecord(value)) {
    return defaultCondition;
  }

  const enabled = typeof value.enabled === 'boolean' ? value.enabled : defaultCondition.enabled;

  const normalizedSettings = Array.isArray(value.settings)
    ? value.settings.map((setting, index) => normalizeSettingItem(setting, index))
    : defaultCondition.settings;

  return {
    enabled,
    settings: normalizedSettings,
  };
};

/**
 * プレースホルダー配列を結合する
 * @param placeholderFieldGroups - 結合対象のプレースホルダー配列群
 */
export const mergePlaceholderFields = (
  ...placeholderFieldGroups: Array<readonly UnmanagedBusinessPlaceholderField[] | UnmanagedBusinessPlaceholderField[] | undefined | null>
): UnmanagedBusinessPlaceholderField[] => {
  return placeholderFieldGroups.flatMap((placeholderFields) => (placeholderFields == null ? [] : [...placeholderFields]));
};

/**
 * 指定フィールドがプレースホルダーかどうかを判定する
 * @param placeholderFields - プレースホルダーフィールド一覧
 * @param field - 判定対象フィールド
 */
export const isPlaceholderField = (
  placeholderFields: readonly UnmanagedBusinessPlaceholderField[] | undefined | null,
  field: UnmanagedBusinessPlaceholderField
): boolean => {
  return placeholderFields?.includes(field) ?? false;
};

/**
 * 表示用にプレースホルダーを空値へ変換する
 * @param value - 変換対象の値
 * @param placeholderFields - プレースホルダーとして扱う項目一覧
 * @param field - 判定対象の項目
 */
export const normalizeUbNullDisplayValue = (
  value: string | undefined | null,
  placeholderFields?: readonly UnmanagedBusinessPlaceholderField[] | null,
  field?: UnmanagedBusinessPlaceholderField
): string | undefined => {
  if (value == null) {
    return undefined;
  }

  if (field != null && isPlaceholderField(placeholderFields, field)) {
    return undefined;
  }

  return value;
};

/**
 * 表示用に容量プレースホルダーを空値へ変換する
 * @param value - 変換対象の値
 * @param placeholderFields - プレースホルダーとして扱う項目一覧
 * @param field - 判定対象の項目
 */
export const normalizeUbNullPackageDisplayValue = (
  value: string | number | undefined | null,
  placeholderFields?: readonly UnmanagedBusinessPlaceholderField[] | null,
  field: UnmanagedBusinessPlaceholderField = 'package'
): string | undefined => {
  if (value == null) {
    return undefined;
  }

  if (isPlaceholderField(placeholderFields, field)) {
    return undefined;
  }

  return String(value);
};
